/**
 * Admin-console theme preference — the half that was missing behind the header
 * toggle (2026-08-23 prod team walkthrough: "the dark/light toggle does
 * nothing").
 *
 * It did nothing literally: the control in the admin topbar was a `<button>`
 * with an aria-label of "Settings", a sun glyph, and NO onClick — see the
 * commit that introduced this file. There was no handler, no stored preference
 * and no dark values for the semantic tokens. This module is the state + the
 * persistence; `styles/design-system.css` (`:root[data-theme='dark']`) is the
 * paint.
 *
 * Three decisions worth keeping:
 *
 *  1. LIGHT IS THE DEFAULT AND STAYS IT. UX spec §2.3 — most trust holders are
 *     older and non-technical, and the product reads as a serious fiduciary
 *     system in light. Dark applies only when a human has explicitly chosen it.
 *     `prefers-color-scheme` is deliberately NOT consulted: someone whose
 *     laptop is dark has expressed a preference about their laptop, not about
 *     where their family trust is administered.
 *
 *  2. IT IS A DOCUMENT-LEVEL ATTRIBUTE, NOT REACT STATE PASSED DOWNWARD.
 *     `data-theme` on <html> is what the CSS selects on, so every surface —
 *     including portals, modals and the toast layer, which render outside the
 *     admin subtree — re-skins together. A context provider would have missed
 *     exactly those.
 *
 *  3. THE WRITE IS GUARDED. localStorage throws in Safari private mode and in
 *     some embedded webviews; a theme preference is not worth a white screen,
 *     so a failed read or write degrades to "light this session" rather than
 *     propagating.
 */

import { useCallback, useEffect, useState } from 'react';

export type AdminTheme = 'light' | 'dark';

/** Namespaced so it cannot collide with the Supabase auth keys on the same
 *  origin (`sb-<ref>-auth-token` and friends). */
const STORAGE_KEY = 'elreign.admin.theme';

/** The attribute `styles/design-system.css` selects on. */
const ATTR = 'data-theme';

function readStored(): AdminTheme {
  if (typeof window === 'undefined') return 'light';
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    // Storage unavailable (private mode / embedded webview) — the default.
    return 'light';
  }
}

function persist(theme: AdminTheme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // The preference simply does not survive the session. Not worth throwing.
  }
}

/** Reflect the choice onto <html> (or remove it, so plain `:root` applies). */
export function applyThemeToDocument(theme: AdminTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') root.setAttribute(ATTR, 'dark');
  else root.removeAttribute(ATTR);
}

/**
 * Read + set the console's theme.
 *
 * Returns the current theme, a toggle, and an explicit setter. The initialiser
 * reads storage during the FIRST render rather than in an effect, so a
 * returning admin who chose dark does not get a light frame painted first and
 * swapped a tick later.
 */
export function useAdminTheme(): {
  theme: AdminTheme;
  toggleTheme: () => void;
  setTheme: (t: AdminTheme) => void;
} {
  const [theme, setThemeState] = useState<AdminTheme>(readStored);

  // Keep the document attribute in step with state, including on first mount —
  // main.tsx does not pre-apply this, so the effect IS the initial application.
  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  const setTheme = useCallback((next: AdminTheme) => {
    setThemeState(next);
    persist(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: AdminTheme = prev === 'dark' ? 'light' : 'dark';
      persist(next);
      return next;
    });
  }, []);

  return { theme, toggleTheme, setTheme };
}
